/**
 * Please prefix constants with `"CLOUD_"`. They are exported via the global constants file at the root
 * of the package.
 */
export const CLOUD_FLAG_NAMES = {
  CLOUD_DEPLOYMENT_WORKER_POC: 'ff-cloud-1477-deployment-worker-poc',
  CLOUD_CLONE_FAILURE_HANDLING: 'ff-cloud-654-clone-failure-handling',
  // Default off. Target customId with the GitHub App installation ID.
  CLOUD_RECORD_GITHUB_INSTALLATION_FAILURES:
    'ff-cloud-869-record-github-installation-failures',
  CLOUD_WORKER_INITIALIZATION_READINESS:
    'ff-cloud-779-worker-initialization-readiness',
  COSMIC_DEVLINK: 'ff-cosmic-159-devlink', // [Cosmic],
  COSMIC_CF_SDK_DEPLOYER: 'ff-cosmic-437-cloudflare-sdk-deployer',
  COSMIC_CLOUDFLARE_CONTAINERS: 'ff-cld-cloudflare-containers',
  COSMIC_CLOUDFLARE_CONTAINER_DEPLOYER:
    'ff-cld-2260-cloudflare-container-deployer',
  CLOUD_AUTO_MANIFEST_UPDATES: 'ff-cld-631-auto-manifest-updates',
  // Changes apply on the next refresh with enableCloudAppManifest enabled.
  // Disabling this flag clears redirects via the builder; legacy patches retain them.
  CLOUD_APP_DEFAULT_DOMAIN_REDIRECT:
    'ff-cloud-1051-app-default-domain-redirect',
  CLOUD_PROJECT_TWEAKS: 'ff-cld-1080-cloud-project-tweaks',
  COSMIC_OAUTH_PUBLIC_API: 'ff-cld-1174-oauth-public-api',
  CLOUD_ENTERPRISE_APP_ENTITLEMENT: 'ff-cld-1416-enterprise-app-entitlement',
  CLOUD_DASHBOARD_SSR: 'ff-cld-2000-dashboard-ssr',
  // CLOUD-909: renders the Dashboard LeftNav from the matched route's
  // `handle.leftNav` during render instead of from `useAdd*` effects, so the
  // nav is present in the SSR document. Off = the effect-driven nav.
  CLOUD_DASHBOARD_DECLARATIVE_LEFT_NAV: 'ff-dashboard-declarative-left-nav',
  CLOUD_STORAGE_DATA_API: 'ff-cloud-982-storage-data-api',
  CLOUD_INHERIT_OMITTED_SSA_DEFAULT: 'ff-cloud-792-inherit-omitted-ssa-default',
  CLOUD_WIZARD_BUILD_LOGS: 'ff-cld-2406-wizard-build-logs',
  CLOUD_STATIC_SITE_HOSTING: 'ff-cld-2158-static-site-hosting',
  CLOUD_IPV4_WEBHOOK_FIX: 'ff-cloud-6-ipv4-webhook-fix',
  CLOUD_SURFACE_PACKAGE_JSON_LABEL_CHECK:
    'ff-cloud-319-surface-package-json-label-check',
  CLOUD_OAUTH_APPS_ALLOWLIST: 'ff-cld-2033-cosmic-apps-oauth-allowlist',
  // Kill-switch for the CLOUD-525 per-user authorization on the cli-* OAuth
  // routes. Default off = enforce; flip on to disable enforcement without a
  // redeploy if it regresses a legitimate CLI deploy flow.
  CLOUD_CLI_OAUTH_AUTHZ_KILL_SWITCH: 'ff-cloud-525-cli-oauth-authz-kill-switch',
  // Kill-switch for the CLOUD-762 initiator-binding/PKCE/one-time-nonce rework
  // of the Cosmic GitHub connect flow. Default off = enforce (new behavior);
  // flip on to fall back to the pre-CLOUD-762 stateless flow without a
  // redeploy if the new flow regresses a legitimate connect attempt.
  //
  // Gotcha for integration tests (mocha, real feature-config, no jest.mock):
  // isTestEnv() evaluates any flag NOT in defaultFalseFlags.ts as true, so
  // this kill switch reads true (legacy) under a real test env unless
  // explicitly stubbed -- the inverse of what most flags mean by "true" in
  // test. A mocha/integration test driving /cosmic/github/* must stub this
  // gate to false explicitly (see CLOUD_CLI_OAUTH_AUTHZ_KILL_SWITCH's own
  // test stub in entrypoints/server/test/oauth/cosmicCliAuthz_test.ts) or it
  // silently exercises the legacy path instead of the fix.
  CLOUD_GITHUB_CONNECT_LEGACY_KILL_SWITCH:
    'ff-cloud-762-github-connect-legacy-kill-switch',
  CLOUD_ENFORCE_ENTITLEMENT_PROJECT_LOCKS:
    'ff-cld-2319-enforce-entitlement-project-locks',
  // Gates the @webflow/app "v2" build engine (COSMIC_BUILD_ENGINE=v2) for cosmic
  // builds — evaluated in both the build-task deployer and the CLI deploy-init.
  // Value is the Statsig gate id; must match verbatim.
  CLOUD_ENABLE_COSMIC_BUILD_ENGINE_V2:
    'ff-cloud-514-enable-cosmic-build-engine-v2',
  // CLOUD-168: compatibility_date policy for Cloud workers. Default off = the
  // template's date, unchanged. On = the date is always a valid date at or above
  // the platform floor: the user's wrangler date when it qualifies, otherwise the
  // template date or the floor, whichever is later. Evaluated in the cosmic
  // BuildRequestController and passed to the build task as COSMIC_COMPAT_DATE_POLICY
  // (the builder container has no Statsig client); the build settles the date and
  // records it, and the deploy applies the record.
  // Value is the Statsig gate id; must match verbatim.
  CLOUD_COMPAT_DATE_POLICY: 'ff-cloud-168-compat-date-policy',
  // CLOUD-1462: serve framework assets from the page host instead of the raw
  // Worker host. Default off = today's behavior. Evaluated per Cloud environment
  // in the cosmic BuildRequestController and passed to the build task as
  // COSMIC_SITE_HOST_ASSETS (the builder container has no Statsig client).
  // Value is the Statsig gate id; must match verbatim.
  CLOUD_SITE_HOST_ASSETS: 'ff-cloud-1462-site-host-assets',
  // Serves a pinned, self-hosted analytics.js (1.83.0) instead of Segment's
  // rolling CDN build. 1.84.x adds an Authorization header that makes every
  // event POST preflighted, which analytics-api.webflow.com cannot answer.
  // Default off; flip on to restore client-side Segment events without a
  // redeploy. Temporary -- revert once the CloudFront fix is applied.
  CLOUD_SELF_HOSTED_SEGMENT_SCRIPT: 'ff-cloud-861-self-hosted-segment-script',
  // Standard rollout for the CLOUD-873 folder-placement validation on
  // standalone Cloud app create. Default off = legacy behavior (an
  // invalid/cross-workspace folder, or a folder combined with siteId, is
  // silently dropped); ramp on to enforce the new 400s.
  CLOUD_COSMIC_APPS_FOLDER_VALIDATION:
    'ff-cloud-873-cosmic-apps-folder-validation',
  // Standard rollout for the CLOUD-1150 name validation on app update and
  // dashboard create. Default off = legacy behavior (any name stored as sent,
  // untrimmed); ramp on to require a trimmed 3–39 character name and return
  // 400 otherwise.
  CLOUD_COSMIC_APPS_UPDATE_NAME_VALIDATION:
    'ff-cloud-1150-cosmic-apps-update-name-validation',
  // Standard rollout for the CLOUD-481 static/framework detection verdict on
  // GitHub-import `getPackageJsonInfo`. Default off = legacy behavior (tree-only
  // fields, no extra GitHub GraphQL blob read, neutral verdict fields); ramp on
  // to read package.json/webflow.json and return the @webflow/app verdict.
  CLOUD_STATIC_IMPORT_DETECTION: 'ff-cloud-481-static-import-detection',
  // Standard rollout for the CLOUD-990 `appPath` field on
  // PATCH /cosmic/apps/:app_id. Default off = legacy behavior (`appPath` is
  // rejected as an unexpected field, with the original error message); ramp on
  // to accept, validate and persist it.
  CLOUD_COSMIC_APPS_UPDATE_APP_PATH: 'ff-cloud-990-cosmic-apps-update-app-path',
  // Standard rollout for the CLOUD-1078 missing-user guard in
  // requireCosmicAccess. Default off = legacy behavior (a non-machine token
  // with no req.user throws a TypeError, surfaced as a 500); ramp on to reject
  // it with NotAllowedForRole.
  CLOUD_COSMIC_ACCESS_REQUIRE_USER: 'ff-cloud-1078-cosmic-access-require-user',
  // Standard rollout (workspaceId) for the CLOUD-1071 domain-validity cache
  // changes on the workspace DNS-warning paths (workspace list, workspace
  // detail with withDnsCheck, invalid-dns-sites). Default off = legacy behavior
  // (every result cached for 5 minutes, no request dedupe, edge hostnames
  // resolved on every check); ramp on to cache valid results for 24h (invalid
  // stays 5 minutes), share one in-flight check per domain, and reuse resolved
  // edge IPs for 5 minutes.
  CLOUD_DOMAIN_VALIDITY_CACHE: 'ff-cloud-1071-domain-validity-cache',
  // Standard rollout (workspaceId) for the CLOUD-1071 limits on live domain
  // checks, layered on CLOUD_DOMAIN_VALIDITY_CACHE (only applies where that
  // flag is also on). Default off = no limits. On: the badge count path reads
  // every cached result in one Redis round trip, returns as soon as one domain
  // is invalid, and runs at most 10 live checks at a time per workspace check;
  // live checks share a per-process cap of 32; invalid-dns-sites also runs at
  // most 10 at a time and is rate limited per user, workspace, and IP.
  CLOUD_DOMAIN_VALIDITY_LIMITS: 'ff-cloud-1071-domain-validity-limits',
  // Standard rollout (userID) for CLOUD-1071 deferred workspace DNS warnings.
  // Default off = GET /api/workspaces computes hasUpdateDNSSites inline, as
  // before, even when the client sends withDnsCheck=false. On: that request
  // skips DNS work and returns domainEnrichment metadata instead, and the
  // client fetches the warnings afterwards from GET /api/workspaces/dns-warnings
  // (one bounded request for every eligible workspace).
  CLOUD_DEFERRED_DNS_WARNINGS: 'ff-cloud-1071-deferred-dns-warnings',
  // Kill switch for the CLOUD-1366 `step()` -> async/await conversion of the
  // flows in models/person.ts. Default off = the original step()-based flows
  // (pre-CLOUD-1366 behavior); turn on to run the callbackify/async
  // implementations. TEMPORARY: delete together with the legacy step()
  // variants once the async flows have rolled out.
  CLOUD_1366_PERSON_ASYNC: 'ff-cloud-1366-person-async',
  // First-deploy orphan guard on cli-deploy-init. Default off = today's
  // behavior: a taken mount still saves the new app, and a later environment
  // failure leaves that app in place. On = reject a taken mount before the
  // app is saved, and delete an app this request just created if environment
  // setup then fails. Value is the Statsig gate id; must match verbatim.
  CLOUD_CLI_DEPLOY_INIT_ORPHAN_GUARD:
    'ff-cloud-1136-cli-deploy-init-orphan-guard',
  // Kill-switch for the CLOUD-1366 async/await conversion of the step()-based
  // flows in models/siteSubscription.ts. Default off = the original step()
  // implementations (pre-PR, verbatim); flip on to run the new
  // callbackify/async implementations. Temporary -- delete with the legacy code
  // once the async paths have soaked.
  CLOUD_1366_SITE_SUBSCRIPTION_ASYNC: 'ff-cloud-1366-site-subscription-async',
  // Kill switch for the CLOUD-1366 async/await conversion of the
  // `models/collaborator.ts` step-based flows. On = the converted async
  // implementations; off = the original step-based code, restored verbatim.
  // TEMPORARY: delete this flag and the `*Legacy` implementations once the
  // async paths have been fully rolled out.
  CLOUD_1366_COLLABORATOR_ASYNC: 'ff-cloud-1366-collaborator-async',
};
